import { Bell, CheckCheck, X } from "lucide-react";
import * as React from "react";

import { type NotificationOut, notificationsApi } from "@/api/notifications";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth";
import { useNotificationRealtimeStore } from "@/stores/notificationRealtime";

/**
 * NotificationBell — bell icon trong TopBar với unread badge.
 *
 * Polling 30s + realtime WS push (P3-S3 Doc 44). Khi WS event đến,
 * `inboxVersion` bump → effect re-fetch ngay không cần đợi polling.
 */
export function NotificationBell(): JSX.Element | null {
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<NotificationOut[]>([]);
  const [unread, setUnread] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const refreshCount = React.useCallback(async () => {
    if (!isAuth) return;
    try {
      const c = await notificationsApi.unreadCount();
      setUnread(c.unread);
    } catch {
      // ignore
    }
  }, [isAuth]);

  const refreshList = React.useCallback(async () => {
    if (!isAuth) return;
    setBusy(true);
    try {
      const list = await notificationsApi.listMyNotifications(false, 30);
      setItems(list);
    } finally {
      setBusy(false);
    }
  }, [isAuth]);

  // Poll unread count
  React.useEffect(() => {
    if (!isAuth) return;
    void refreshCount();
    const t = window.setInterval(() => void refreshCount(), 30_000);
    return () => window.clearInterval(t);
  }, [isAuth, refreshCount]);

  // P3-S3: realtime bump — WS push notification mới → refresh count + list (nếu open)
  const inboxVersion = useNotificationRealtimeStore((s) => s.inboxVersion);
  React.useEffect(() => {
    if (!isAuth || inboxVersion === 0) return;
    void refreshCount();
    if (open) void refreshList();
  }, [inboxVersion, isAuth, open, refreshCount, refreshList]);

  // Click outside closes
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  if (!isAuth) return null;

  const handleOpen = async (): Promise<void> => {
    if (!open) {
      setOpen(true);
      await refreshList();
    } else {
      setOpen(false);
    }
  };

  const handleMarkRead = async (id: string): Promise<void> => {
    try {
      await notificationsApi.markRead(id);
      await Promise.all([refreshList(), refreshCount()]);
    } catch {
      // ignore
    }
  };

  const handleMarkAllRead = async (): Promise<void> => {
    try {
      await notificationsApi.markAllRead();
      await Promise.all([refreshList(), refreshCount()]);
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => void handleOpen()}
        aria-label="Notifications"
      >
        <Bell className="size-4" />
        {unread > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div className="absolute right-0 mt-2 w-96 max-h-[500px] rounded-md border bg-popover shadow-lg z-50 overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <span className="text-sm font-semibold">Thông báo</span>
            <div className="flex items-center gap-1">
              {unread > 0 ? (
                <button
                  className="text-xs text-primary hover:underline"
                  onClick={() => void handleMarkAllRead()}
                >
                  <CheckCheck className="size-3 inline mr-0.5" />
                  Đánh dấu đã đọc
                </button>
              ) : null}
              <button
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {busy ? (
              <p className="p-4 text-sm text-muted-foreground">Đang tải...</p>
            ) : items.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">
                Chưa có thông báo nào.
              </p>
            ) : (
              <ul className="divide-y">
                {items.map((it) => (
                  <li
                    key={it.id}
                    className={
                      "px-3 py-2 cursor-pointer hover:bg-muted text-sm " +
                      (!it.read_at ? "bg-muted/50" : "")
                    }
                    onClick={() => void handleMarkRead(it.id)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        {it.rendered?.subject ? (
                          <div className="font-medium truncate">
                            {it.rendered.subject}
                          </div>
                        ) : null}
                        <div
                          className="text-xs text-muted-foreground line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: it.rendered?.body ?? "",
                          }}
                        />
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {new Date(it.created_at).toLocaleString("vi-VN")} ·{" "}
                          {it.channel}
                        </div>
                      </div>
                      {!it.read_at ? (
                        <span className="size-2 rounded-full bg-primary shrink-0 mt-1" />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
